import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Brain, Moon, Sparkles, Waves, BookOpen, Wind, Compass,
  ChevronRight, Play, TrendingUp,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// "What do you need right now?" chips — map each mood/need to the best
// existing route. Chips keep the app feeling native to a person's state
// instead of demanding they know the taxonomy.
const NEED_CHIPS = [
  { id: 'stress',     label: 'Stress',     to: '/category/anxiety',            accent: '#c85f5f' },
  { id: 'sleep',      label: 'Sleep',      to: '/category/sleep',              accent: '#5d8aa8' },
  { id: 'healing',    label: 'Healing',    to: '/healing',                      accent: '#8aa985' },
  { id: 'focus',      label: 'Focus',      to: '/breathing',                    accent: '#D6AD60' },
  { id: 'grief',      label: 'Grief',      to: '/grief',                        accent: '#7a6e94' },
  { id: 'confidence', label: 'Confidence', to: '/manifest/self-worth',          accent: '#d4a952' },
  { id: 'peace',      label: 'Peace',      to: '/manifest/peace',               accent: '#8aa985' },
  { id: 'work',       label: 'Work',       to: '/breathing',                    accent: '#5d8aa8' },
];

// Section directory — routes users to full-tab pages for each content type.
// Each entry carries a photo thumbnail so the list reads as a hand-picked
// collection rather than a plain settings menu.
const SECTIONS = [
  { id: 'meditations',    label: 'Meditations',     to: '/category/guided-meditations', Icon: Brain,     image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=240&h=240&fit=crop' },
  { id: 'affirmations',   label: 'Affirmations',    to: '/category/affirmations',       Icon: Sparkles,  image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=240&h=240&fit=crop' },
  { id: 'sleep',          label: 'Sleep',           to: '/category/sleep',              Icon: Moon,      image: 'https://images.unsplash.com/photo-1502635385003-ee1e6a1a742d?w=240&h=240&fit=crop' },
  { id: 'soundscapes',    label: 'Soundscapes',     to: '/sounds',                      Icon: Waves,     image: 'https://images.unsplash.com/photo-1439405326854-014607f694d7?w=240&h=240&fit=crop' },
  { id: 'programs',       label: 'Programs',        to: '/programs',                    Icon: BookOpen,  image: 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=240&h=240&fit=crop' },
  { id: 'workplace',      label: 'Workplace Reset', to: '/workplace-reset',              Icon: Wind,      image: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?w=240&h=240&fit=crop' },
  { id: 'manifestation',  label: 'Manifestation',   to: '/manifest',                    Icon: Sparkles,  image: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=240&h=240&fit=crop' },
];

// Mood → personalized recommendation categories. Keys should match the
// mood IDs saved by MoodCheckInCard (both current and legacy).
const MOOD_TO_CATEGORY = {
  stressed: 'anxiety',
  anxious:  'anxiety',
  low:      'guided-meditations',
  tired:    'sleep',
  okay:     'guided-meditations',
  reflective: 'affirmations',
  good:     'affirmations',
  happy:    'affirmations',
  peaceful: 'affirmations',
  calm:     'affirmations',
  grateful: 'affirmations',
};

const MOOD_LABEL = {
  stressed: 'Stressed',
  anxious: 'Anxious',
  low: 'Low',
  tired: 'Tired',
  okay: 'Okay',
  reflective: 'Reflective',
  good: 'Good',
  happy: 'Happy',
  peaceful: 'Peaceful',
  calm: 'Calm',
  grateful: 'Grateful',
};

const ExplorePage = () => {
  const { user, readPosts } = useAuth();
  const { posts, categories } = useContent();
  const [todaysMood, setTodaysMood] = useState(null);

  // Fetch the user's most recent mood check-in for personalized "Because
  // You Feel …" recommendations.
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('rbr_token');
    fetch(`${API_URL}/api/mood/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setTodaysMood(d?.entry?.mood || null))
      .catch(() => {});
  }, [user]);

  // Continue Listening — the last few posts the user actually opened.
  const continueListening = useMemo(() => {
    if (!readPosts?.length || !posts?.length) return [];
    const byId = new Map(posts.map((p) => [p.id, p]));
    const seen = new Set();
    const out = [];
    for (let i = readPosts.length - 1; i >= 0 && out.length < 3; i -= 1) {
      const p = byId.get(readPosts[i]);
      if (p && !seen.has(p.id)) {
        out.push(p);
        seen.add(p.id);
      }
    }
    return out;
  }, [readPosts, posts]);

  // Popular This Week — pick the highest-liked posts, cap at 3.
  const popular = useMemo(() => {
    if (!posts?.length) return [];
    return [...posts]
      .sort((a, b) => (b.likes || 0) - (a.likes || 0))
      .slice(0, 3);
  }, [posts]);

  // Because You Feel … — 3 recommendations from the category the mood maps to.
  const moodRecommendations = useMemo(() => {
    if (!todaysMood || !posts?.length) return [];
    const catId = MOOD_TO_CATEGORY[todaysMood];
    if (!catId) return [];
    return posts.filter((p) => p.categoryId === catId).slice(0, 3);
  }, [todaysMood, posts]);

  return (
    <div className="pb-8" data-testid="explore-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 animate-fade-in">
        <div className="flex items-center gap-2 mb-1">
          <Compass className="w-4 h-4 text-[#D6AD60]" />
          <p className="rbr-eyebrow">Explore</p>
        </div>
        <h1 className="rbr-heading text-3xl leading-tight">
          What do you need right now?
        </h1>
      </div>

      {/* Need chips — a 4x2 grid so the whole set is above the fold on
          any phone. Each chip is a full-width tap target with a warm
          accent border in its mood color. */}
      <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
        <div className="grid grid-cols-4 gap-2">
          {NEED_CHIPS.map((c) => (
            <Link
              key={c.id}
              to={c.to}
              data-testid={`explore-need-${c.id}`}
              className="flex items-center justify-center px-2 py-3 rounded-2xl bg-[#1F4C40] border transition-all active:scale-95 hover:-translate-y-0.5 hover:shadow-md"
              style={{
                borderColor: `${c.accent}55`,
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
              }}
            >
              <span className="text-xs font-semibold text-[#F8F6F0]">{c.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Continue Listening */}
      {continueListening.length > 0 && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
          <p className="rbr-eyebrow mb-3">Continue Listening</p>
          <div className="space-y-2">
            {continueListening.map((p) => (
              <Link
                key={p.id}
                to={`/post/${p.id}`}
                data-testid={`explore-continue-${p.id}`}
                className="flex items-center gap-3 bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt=""
                    loading="lazy"
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#E8DDCB] flex items-center justify-center flex-shrink-0">
                    <Play className="w-4 h-4 text-[#D6AD60]" fill="currentColor" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#F8F6F0] truncate">{p.title}</p>
                  <p className="text-[11px] text-[#A3BEAF] truncate">
                    {p.category}{p.duration ? ` · ${p.duration}` : ''}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#7A9B8C] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Section directory */}
      <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <div className="bg-[#1F4C40] border border-[#2E6353] rounded-2xl overflow-hidden shadow-sm">
          {SECTIONS.map((s, idx) => (
              <Link
                key={s.id}
                to={s.to}
                data-testid={`explore-section-${s.id}`}
                className={`flex items-center gap-3 px-3 py-3 hover:bg-[#E8DDCB]/50 transition-colors ${
                  idx !== SECTIONS.length - 1 ? 'border-b border-stone-100' : ''
                }`}
              >
                <img
                  src={s.image}
                  alt=""
                  loading="lazy"
                  className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-[#D6AD60]/25"
                />
                <span className="flex-1 text-sm font-semibold text-[#F8F6F0]">
                  {s.label}
                </span>
                <ChevronRight className="w-4 h-4 text-[#7A9B8C]" />
              </Link>
            ))}
        </div>
      </div>

      {/* Popular This Week */}
      {popular.length > 0 && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-3.5 h-3.5 text-[#D6AD60]" />
            <p className="rbr-eyebrow">Popular This Week</p>
          </div>
          <div className="space-y-2">
            {popular.map((p) => (
              <Link
                key={p.id}
                to={`/post/${p.id}`}
                data-testid={`explore-popular-${p.id}`}
                className="flex items-center gap-3 bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt=""
                    loading="lazy"
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#E8DDCB] flex items-center justify-center flex-shrink-0">
                    <Play className="w-4 h-4 text-[#D6AD60]" fill="currentColor" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#F8F6F0] truncate">{p.title}</p>
                  <p className="text-[11px] text-[#A3BEAF] truncate">
                    {p.category}{p.likes ? ` · ${p.likes} ❤` : ''}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#7A9B8C] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Because You Feel {mood} — personalized recommendations */}
      {moodRecommendations.length > 0 && (
        <div className="px-5 mb-2 animate-fade-in-up" style={{ animationDelay: '0.14s' }}>
          <p className="rbr-eyebrow mb-3">
            Because You Feel {MOOD_LABEL[todaysMood] || todaysMood}
          </p>
          <div className="space-y-2">
            {moodRecommendations.map((p) => (
              <Link
                key={p.id}
                to={`/post/${p.id}`}
                data-testid={`explore-because-${p.id}`}
                className="flex items-center gap-3 bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                {p.image ? (
                  <img
                    src={p.image}
                    alt=""
                    loading="lazy"
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[#E8DDCB] flex items-center justify-center flex-shrink-0">
                    <Play className="w-4 h-4 text-[#D6AD60]" fill="currentColor" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#F8F6F0] truncate">{p.title}</p>
                  <p className="text-[11px] text-[#A3BEAF] truncate">
                    {p.category}{p.duration ? ` · ${p.duration}` : ''}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#7A9B8C] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
